import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';

const PILLARS = [
  { icon: 'fa-lightbulb', title: 'Curiosity', text: 'Every child learns through observation, hands-on activities and an inquiry-based approach that keeps wonder alive.' },
  { icon: 'fa-paintbrush', title: 'Creativity', text: 'Open-ended exploration and the arts encourage original thinking and confident self-expression.' },
  { icon: 'fa-brain', title: 'Critical Thinking', text: 'Deep conceptual understanding and reasoning help students analyse, question and solve real problems.' },
  { icon: 'fa-puzzle-piece', title: 'Problem Solving', text: 'Experiential learning builds the skills and confidence to apply knowledge in the real world.' },
];

const DEFAULT_SECTIONS = {
  hero: {
    key: 'hero',
    type: 'hero',
    image: '/images/curriculum-banner.webp',
    image_alt: 'Curriculum and Infrastructure of St. Andrews World School, Indirapuram',
  },
  intro: {
    key: 'intro',
    type: 'intro',
    kicker: 'Our Curriculum',
    title: 'Learning is a Joyful Journey of Discovery',
    description:
      'At St. Andrews World School, we believe that learning is a joyful journey that encourages the active participation of every child. We provide a child-centric learning environment where students learn through observation, hands-on activities and an inquiry-based approach.\n\nOur curriculum blends the best practices of modern education with a strong academic foundation, emphasising conceptual understanding, experiential learning and holistic development — while keeping unnecessary academic pressure at bay.\n\nOur goal is to help every child develop confidence, a love for learning and the skills needed to succeed in the real world.',
    image: '/images/coactivities/curriculum-1.jpg',
    image_alt: 'Curriculum at St. Andrews World School, Indirapuram',
  },
  pillars_header: {
    key: 'pillars_header',
    type: 'header',
    kicker: 'What We Nurture',
    title: 'Curiosity. Creativity. Critical Thought.',
  },
  ...PILLARS.reduce((acc, item, idx) => {
    acc[`pillar_${idx}`] = { key: `pillar_${idx}`, type: 'card', icon: item.icon, title: item.title, description: item.text };
    return acc;
  }, {}),
  cta: {
    key: 'cta',
    type: 'cta',
    icon: 'fa-trophy',
    badge_text: 'Excellence in Modern Education',
    title: 'A Curriculum That Inspires. An Environment That Protects.',
    description: 'Discover the ICSE learning pathway of St. Andrews World School, Indirapuram.',
    button_text: 'Explore Our Infrastructure',
    button_url: '/infrastructure',
    button2_text: 'Enquire for Admission',
    button2_url: '/admission',
  },
};

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const allDefaults = () => Object.values(DEFAULT_SECTIONS).map((s) => ({ id: 0, ...s }));

const Curriculum = () => {
  const title = 'Curriculum and Infrastructure';
  const [sections, setSections] = useState(allDefaults());

  useEffect(() => {
    let mounted = true;
    const fetchSections = async () => {
      try {
        const res = await API.get('/curriculum/sections');
        if (!mounted) return;
        if (res.data?.data?.length) {
          setSections(res.data.data);
        }
      } catch (err) {
        /* fall back to defaults */
      }
    };
    fetchSections();
    return () => { mounted = false; };
  }, []);

  const byKey = sections.reduce((acc, s) => {
    acc[s.section_key] = s;
    return acc;
  }, {});
  const hero = byKey.hero || DEFAULT_SECTIONS.hero;
  const intro = byKey.intro || DEFAULT_SECTIONS.intro;
  const header = byKey.pillars_header || DEFAULT_SECTIONS.pillars_header;
  const cards = sections.filter((s) => s.section_type === 'card').length
    ? sections.filter((s) => s.section_type === 'card')
    : [DEFAULT_SECTIONS.pillar_0, DEFAULT_SECTIONS.pillar_1, DEFAULT_SECTIONS.pillar_2, DEFAULT_SECTIONS.pillar_3];
  const cta = byKey.cta || DEFAULT_SECTIONS.cta;

  const introParas = (intro.description || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const ctaButton = cta.button_url ? (cta.button_url.startsWith('http') ? { href: cta.button_url } : { to: cta.button_url }) : null;
  const ctaButton2 = cta.button2_url ? (cta.button2_url.startsWith('http') ? { href: cta.button2_url } : { to: cta.button2_url }) : null;

  return (
    <>
      <SEO
        title={title}
        description="Curriculum and Infrastructure of St. Andrews World School, Indirapuram — a child-centric ICSE curriculum with world-class facilities."
        keywords="Curriculum, Infrastructure, SAWS Indirapuram, ICSE curriculum"
      />

      <section className="page-hero">
        <img
          src={resolveImage(hero.image)}
          alt={hero.image_alt || title}
          className="page-hero-img"
          loading="eager"
        />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner" />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-16 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute top-24 -right-24 w-96 h-96 rounded-full bg-[#1B3160]/5 blur-3xl" aria-hidden />
        <div className="absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-[#D4A73E]/10 blur-3xl" aria-hidden />
        <div className="container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 md:gap-16 items-center">
            <Reveal className="relative max-lg:order-2">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#1B3160]/30 to-[#D4A73E]/20 rounded-3xl blur-lg opacity-60" aria-hidden />
              <img
                src={resolveImage(intro.image)}
                alt={intro.image_alt || intro.title}
                className="relative w-full aspect-[4/3] object-cover rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </Reveal>
            <div className="max-lg:order-1">
              <Reveal>
                <div className="kicker">{intro.kicker}</div>
                <h2 className="text-[30px] md:text-[38px] font-bold text-primary mt-2 leading-tight">
                  {intro.title}
                </h2>
                <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full my-6"></div>
              </Reveal>
              <div className="space-y-5">
                {introParas.map((para, index) => (
                  <Reveal key={index} delayMs={100 + index * 60}>
                    <p className="text-body text-[16px] md:text-[17px] leading-relaxed">
                      {para}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-16 md:py-20 bg-light">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="kicker">{header.kicker}</div>
              <h2 className="text-[26px] md:text-[32px] font-bold text-primary mt-2 leading-tight">
                {header.title}
              </h2>
              <div className="w-14 h-[3px] bg-[#D4A73E] rounded-full mx-auto mt-4"></div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {cards.map((item, index) => (
              <Reveal key={item.id || item.section_key || index} delayMs={index * 80}>
                <div className="card p-7 h-full group hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                  <div className="absolute -right-10 -top-10 w-28 h-28 rounded-full bg-secondary/15" aria-hidden />
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                    <i className={`fas ${item.icon}`} aria-hidden />
                  </div>
                  <h3 className="text-primary font-bold text-lg leading-snug mt-5">{item.title}</h3>
                  <p className="text-body text-[15px] leading-relaxed mt-3">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#1B3260] relative overflow-hidden text-white">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '24px 24px' }} aria-hidden />
        <div className="container relative">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-[#F3D27E] text-[#0d1b3e] px-5 py-2 rounded-full text-sm font-semibold mb-6">
                {cta.icon && <i className={`fas ${cta.icon}`} aria-hidden />} {cta.badge_text}
              </div>
              <h2 className="text-[26px] md:text-[34px] font-bold text-white leading-tight">
                {cta.title}
              </h2>
              <p className="text-white/70 text-[16px] md:text-[17px] leading-relaxed mt-4">
                {cta.description}
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
                {ctaButton &&
                  (ctaButton.href ? (
                    <a href={ctaButton.href} className="btn-gold">
                      {cta.button_text}
                      <i className="fas fa-arrow-right text-[12px]" aria-hidden />
                    </a>
                  ) : (
                    <Link to={ctaButton.to} className="btn-gold">
                      {cta.button_text}
                      <i className="fas fa-arrow-right text-[12px]" aria-hidden />
                    </Link>
                  ))}
                {ctaButton2 &&
                  (ctaButton2.href ? (
                    <a
                      href={ctaButton2.href}
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold border border-white/30 text-white bg-white/10 backdrop-blur hover:bg-white/15 hover:border-white/40 transition-all duration-300"
                    >
                      {cta.button2_text}
                    </a>
                  ) : (
                    <Link
                      to={ctaButton2.to}
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold border border-white/30 text-white bg-white/10 backdrop-blur hover:bg-white/15 hover:border-white/40 transition-all duration-300"
                    >
                      {cta.button2_text}
                    </Link>
                  ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Curriculum;